import { Component } from '@theme/component';
import { debounce, onAnimationEnd } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * A custom element that manages the full-screen search interface.
 * Nike-style header takeover with logo, search bar, and cancel button.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} backdrop - The backdrop overlay element.
 * @property {HTMLElement} container - The main header container.
 * @property {HTMLButtonElement} closeButton - The cancel button element.
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLButtonElement} clearButton - The clear/reset button element.
 * @property {HTMLElement} resultsContainer - The results container element.
 * @property {HTMLElement} resultsInner - The inner results container.
 * @property {HTMLElement} [popularCollections] - The popular collections container (optional).
 *
 * @extends Component<Refs>
 */
class SearchFullscreenComponent extends Component {
  requiredRefs = ['backdrop', 'container', 'closeButton', 'searchInput', 'resultsContainer', 'resultsInner'];

  /** @returns {boolean} */
  get showRecentlyViewed() {
    return this.dataset.showRecentlyViewed !== 'false';
  }

  /** @returns {boolean} */
  get showPopularCollections() {
    return this.dataset.showPopularCollections === 'true';
  }

  /** @returns {boolean} */
  get showTermWithCount() {
    return this.dataset.showTermWithCount === 'true';
  }

  #scrollY = 0;
  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('keydown', this.#handleGlobalKeyDown, { signal });
    document.addEventListener('click', this.#handleSearchButtonClick, { signal });
  }

  /**
   * Handles clicks on search buttons.
   * @param {MouseEvent} event - The click event.
   */
  #handleSearchButtonClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const searchButton = target.closest('[data-open-fullscreen-search]');

    if (!searchButton) return;

    event.preventDefault();
    event.stopPropagation();
    this.showSearch();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#restoreBodyScroll();
  }

  /**
   * Handles global keyboard shortcuts.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleGlobalKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      if (this.dataset.open === 'true') {
        this.closeSearch();
      } else {
        this.showSearch();
      }
      return;
    }

    if (event.key === 'Escape' && this.dataset.open === 'true') {
      event.preventDefault();
      this.closeSearch();
    }
  };

  /**
   * Handles keyboard events on the search input.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const form = this.refs.searchInput.closest('form');
      if (form && this.refs.searchInput.value.trim()) {
        form.submit();
      }
    }
  };

  /**
   * Shows the full-screen search.
   */
  showSearch = () => {
    if (this.dataset.open === 'true') return;

    this.#scrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;
    document.documentElement.setAttribute('scroll-lock', '');

    this.dataset.open = 'true';
    this.dataset.hasResults = 'false';

    requestAnimationFrame(() => {
      setTimeout(() => {
        this.refs.searchInput?.focus();
      }, 100);
    });

    if (this.showRecentlyViewed) {
      this.#loadRecentlyViewed();
    }

    this.dispatchEvent(new CustomEvent('search-fullscreen:open'));
  };

  /**
   * Closes the full-screen search.
   */
  closeSearch = async () => {
    if (this.dataset.open !== 'true') return;

    this.classList.add('search-fullscreen--closing');

    await new Promise((resolve) => setTimeout(resolve, 300));

    this.dataset.open = 'false';
    this.dataset.hasResults = 'false';
    this.classList.remove('search-fullscreen--closing');

    this.#restoreBodyScroll();

    this.refs.searchInput.value = '';
    this.refs.clearButton.hidden = true;
    this.refs.resultsInner.innerHTML = '';

    this.dispatchEvent(new CustomEvent('search-fullscreen:close'));
  };

  /**
   * Clears the search input and results.
   */
  clearSearch = () => {
    this.refs.searchInput.value = '';
    this.refs.clearButton.hidden = true;
    this.refs.searchInput.focus();
    this.dataset.hasResults = 'false';

    if (this.showRecentlyViewed) {
      this.#loadRecentlyViewed();
    } else {
      this.refs.resultsInner.innerHTML = '';
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const resultsInner = this.refs.resultsInner;
    if (!resultsInner) return;

    const recentlyViewedWrapper = resultsInner.querySelector('.recently-viewed-wrapper');
    if (!recentlyViewedWrapper) return;

    recentlyViewedWrapper.classList.add('removing');

    onAnimationEnd(recentlyViewedWrapper, () => {
      recentlyViewedWrapper.remove();
      // Clear the results inner if there's nothing left
      if (resultsInner.children.length === 0) {
        resultsInner.innerHTML = '';
      }
    });
  }

  /**
   * Handles search input changes.
   */
  handleSearch = debounce(() => {
    const searchTerm = this.refs.searchInput.value.trim();

    this.refs.clearButton.hidden = searchTerm.length === 0;

    if (searchTerm.length === 0) {
      this.dataset.hasResults = 'false';

      if (this.showRecentlyViewed) {
        this.#loadRecentlyViewed();
      } else {
        this.refs.resultsInner.innerHTML = '';
      }
      return;
    }

    this.#fetchSearchResults(searchTerm);
  }, 200);

  /**
   * Fetches search results from the predictive search endpoint.
   * @param {string} searchTerm - The search term.
   */
  async #fetchSearchResults(searchTerm) {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,variants.barcode,vendor'
    );

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML('predictive-search', false, url);

      if (abortController.signal.aborted) return;

      if (resultsMarkup) {
        this.refs.resultsInner.innerHTML = resultsMarkup;
        this.dataset.hasResults = 'true';
        this.#checkAndUpdateProductResults(searchTerm);
        this.#initializeComponents();
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.error('Search error:', error);
      }
    }
  }

  /**
   * Loads recently viewed products.
   */
  async #loadRecentlyViewed() {
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length === 0) {
      this.refs.resultsInner.innerHTML = '';
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML('predictive-search', false, url);
      if (resultsMarkup) {
        this.refs.resultsInner.innerHTML = resultsMarkup;
        RecentlyViewed.syncFromRecentlyViewedMarkup(this.refs.resultsInner);
        this.#initializeComponents();
      }
    } catch (error) {
      console.error('Error loading recently viewed:', error);
    }
  }

  /**
   * Checks if product results are shown and replaces the title with search term + count if enabled.
   * @param {string} searchTerm - The search term.
   */
  #checkAndUpdateProductResults(searchTerm) {
    if (!this.showTermWithCount) {
      return;
    }

    const resultsInner = this.refs.resultsInner;
    if (!resultsInner) return;

    const productWrapper = resultsInner.querySelector(
      '.predictive-search-results__wrapper-products:not(.recently-viewed-wrapper .predictive-search-results__wrapper-products)',
    );

    if (!productWrapper) return;

    const productTitle = Array.from(resultsInner.querySelectorAll('.predictive-search-results__title')).find(
      (title) =>
        !title.closest('.recently-viewed-wrapper') &&
        title.nextElementSibling === productWrapper &&
        productWrapper.classList.contains('predictive-search-results__wrapper-products'),
    );

    if (!productTitle) return;

    const productCount = productWrapper.querySelectorAll('.predictive-search-results__card--product').length;

    if (productCount > 0) {
      let termDisplay = resultsInner.querySelector('.search-fullscreen__term-display');
      if (!termDisplay && productTitle.parentNode) {
        termDisplay = document.createElement('h4');
        termDisplay.className = 'search-fullscreen__term-display predictive-search-results__title';
        productTitle.parentNode.insertBefore(termDisplay, productTitle);
      }

      if (!termDisplay) return;

      const termText = termDisplay.querySelector('.search-fullscreen__term-text') || document.createElement('span');
      const termCount = termDisplay.querySelector('.search-fullscreen__term-count') || document.createElement('span');

      if (!termText.classList.contains('search-fullscreen__term-text')) {
        termText.className = 'search-fullscreen__term-text';
      }
      if (!termCount.classList.contains('search-fullscreen__term-count')) {
        termCount.className = 'search-fullscreen__term-count';
      }

      termText.textContent = searchTerm;
      termCount.textContent = ` (${productCount})`;

      if (!termDisplay.contains(termText)) {
        termDisplay.appendChild(termText);
      }
      if (!termDisplay.contains(termCount)) {
        termDisplay.appendChild(termCount);
      }

      if (productTitle instanceof HTMLElement) {
        productTitle.style.display = 'none';
      }
    }
  }

  /**
   * Initializes custom elements in the results container.
   */
  #initializeComponents() {
    // Upgrade any custom elements that haven't been initialized
    const container = this.refs.resultsInner;
    if (container) {
      // Find all custom elements and ensure they're upgraded
      container.querySelectorAll('*').forEach((el) => {
        if (el.tagName.includes('-') && !el.matches(':defined')) {
          customElements.upgrade(el);
        }
      });

      // Trigger resize to ensure slideshows recalculate their dimensions
      window.dispatchEvent(new Event('resize'));
    }
  }

  /**
   * Restores body scroll after closing search.
   */
  #restoreBodyScroll() {
    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');

    if (scrollY) {
      window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });
    }
  }
}

if (!customElements.get('search-fullscreen-component')) {
  customElements.define('search-fullscreen-component', SearchFullscreenComponent);
}
